import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo
} from 'react';
import {
  Link,
  useLocation,
  useNavigate
} from 'react-router-dom';
import {
  useClickAnyWhere,
  useMediaQuery
} from 'usehooks-ts';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import {
  Eye,
  Settings,
  MessageSquare,
  MessagesSquare,
  BookOpen,
  Layers,
  FileText,
  Shield
} from 'lucide-react';
import getInitials from '@lok/utils/getInitials';
import { getTimeDifference } from '@lok/utils/formatDate';
import { useTheme } from '@lok/context/ThemeContext';
import { useAdmin } from '@lok/context/AdminContext';
import LOGO_WHITE from '@lok/assets/ContinuumLogoWhite.svg';
import LOGO_BLACK from '@lok/assets/ContinuumLogoBlack.svg';
import ThemeSwitcher from '@lok/components/Utility/ThemeSwitcher';
import EditableLogo from '@lok/components/admin/EditableLogo';
import PageContent from '@lok/components/Layouts/PageContent';
import LearnerSearch from '@lok/components/search/LearnerSearch';
import {
  useFetchTotalUnreadQuery,
  useFetchUnreadDetailsQuery,
  useMarkAllThreadsReadMutation
} from '@lok/services/discussions';
import { useMarkThreadAsReadMutation } from '@lok/services/discussions/thread';
import useModuleStore from '../modules/store/lessonStore';
import useAuth from '../../hooks/useAuth';

// ============================================================================
// Constants
// ============================================================================

const SCROLL_INDICATOR_HEIGHT = 10;
const SCROLL_INDICATOR_COLOR = 'rgb(var(--brand-secondary))';
const SCROLL_CONTAINER_SELECTOR = '[data-lesson-content]';
const UNREAD_DETAILS_LIMIT = 10;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Finds the most appropriate university with a logo based on priority:
 * 1. Whitelabeled universities from admin access
 * 2. Whitelabeled enrolled universities (students)
 * 3. Primary university with logo
 * 4. Any enrolled university with logo
 * 5. Non-whitelabeled admin universities with logo
 * 6. Primary university (fallback)
 *
 * Super-admins (user.type === 'superuser') always see the Continuum logo —
 * we return null here so the navbar falls through to the bundled fallback.
 * Without this, a super-admin who happens to be listed as admin of a
 * whitelabeled client would resolve to that client's logo, and any logo edit
 * via the navbar's EditableLogo would silently patch the wrong client.
 * Super-admins manage client logos from the per-client Branding modal instead.
 */
const findUniversityWithLogo = (user) => {
  if (!user) return null;
  if (user.type === 'superuser') return null;

  const hasLogo = (uni) =>
    uni?.logo || uni?.logo_theme_dark;
  const isWhitelabeled = (uni) => uni?.is_whitelabel;

  // Priority 1: Whitelabeled admin universities
  const adminUniversities =
    user.admins?.map((admin) => admin.university) ?? [];
  const whitelabeledAdmin = adminUniversities.find(
    (uni) => isWhitelabeled(uni) && hasLogo(uni)
  );
  if (whitelabeledAdmin) return whitelabeledAdmin;

  // Priority 2: Whitelabeled enrolled universities
  const enrolledUniversities =
    user.enrolled_universities ?? [];
  const whitelabeledEnrolled = enrolledUniversities.find(
    (uni) => isWhitelabeled(uni) && hasLogo(uni)
  );
  if (whitelabeledEnrolled) return whitelabeledEnrolled;

  // Priority 3: Primary university with logo
  if (hasLogo(user.university)) return user.university;

  // Priority 4: Any enrolled university with logo
  const enrolledWithLogo =
    enrolledUniversities.find(hasLogo);
  if (enrolledWithLogo) return enrolledWithLogo;

  // Priority 5: Non-whitelabeled admin universities with logo
  const adminWithLogo = adminUniversities.find(
    (uni) => !isWhitelabeled(uni) && hasLogo(uni)
  );
  if (adminWithLogo) return adminWithLogo;

  // Priority 6: Fallback to primary university
  return user.university ?? null;
};

/**
 * Returns the appropriate logo URL based on theme and available logos.
 * If only one theme's logo is set, the matching theme uses it and the
 * other theme falls back to the bundled Continuum logo (so a light-only
 * upload doesn't render dark-on-dark in dark mode and vice versa).
 */
const getLogo = (university, isDarkMode) => {
  const { logo: lightLogo, logo_theme_dark: darkLogo } =
    university ?? {};
  const fallback = isDarkMode ? LOGO_WHITE : LOGO_BLACK;
  const themeLogo = isDarkMode ? darkLogo : lightLogo;
  return themeLogo || fallback;
};

// ============================================================================
// Components
// ============================================================================

/**
 * Renders the navbar logo. Prefers the remote `src`; if it's empty/missing or
 * fails to load, falls back to the bundled Continuum SVG. The fallback is
 * tracked in React state (rather than mutating `e.currentTarget.src`) so the
 * empty-src case renders the fallback immediately with no failed request.
 */
const NavbarLogo = ({ src, fallbackSrc, alt }) => {
  const [hasErrored, setHasErrored] = useState(false);

  useEffect(() => {
    setHasErrored(false);
  }, [src]);

  const shouldUseRemote =
    typeof src === 'string' && src.length > 0 && !hasErrored;
  const resolvedSrc = shouldUseRemote ? src : fallbackSrc;
  // SVGs often ship with no intrinsic size (only a viewBox, or width/height
  // "100%"), so a bare `max-height` resolves them to 0px and the logo renders
  // blank. Give SVG sources a *definite* height instead; raster logos keep the
  // existing `max-h` behavior so their sizing is unchanged.
  const isSvg =
    typeof resolvedSrc === 'string' && /\.svg(\?|#|$)/i.test(resolvedSrc);

  return (
    <img
      src={resolvedSrc}
      onError={
        shouldUseRemote ? () => setHasErrored(true) : undefined
      }
      className={clsx(
        'cursor-pointer duration-500 max-w-[110px] md:max-w-[300px] object-contain dark:bg-transparent py-2 md:px-4 md:py-2.5',
        isSvg
          ? 'h-16 md:h-[5.16rem] w-auto'
          : 'max-h-16 md:max-h-[5.16rem]'
      )}
      alt={alt}
    />
  );
};

// ============================================================================
// Custom Hooks
// ============================================================================

/**
 * Hook to manage scroll progress for lesson pages with container-based scrolling
 */
const useLessonScrollProgress = (
  isLessonPage,
  pathname
) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!isLessonPage) {
      setProgress(0);
      return undefined;
    }

    let scrollContainer = null;
    let resizeObserver = null;
    let pollId = null;

    const updateProgress = () => {
      if (!scrollContainer) return;
      const { scrollTop, scrollHeight, clientHeight } =
        scrollContainer;
      const totalScroll = scrollHeight - clientHeight;
      setProgress(
        totalScroll > 0 ? scrollTop / totalScroll : 0
      );
    };

    const attach = (el) => {
      scrollContainer = el;
      updateProgress();
      el.addEventListener('scroll', updateProgress, {
        passive: true
      });
      resizeObserver = new ResizeObserver(updateProgress);
      resizeObserver.observe(el);
    };

    // Lesson content can load async, so the container may not exist yet
    // on the first tick after route change. Poll briefly until it appears.
    let attempts = 0;
    const tryAttach = () => {
      const el = document.querySelector(
        SCROLL_CONTAINER_SELECTOR
      );
      if (el) {
        attach(el);
        return true;
      }
      return false;
    };

    if (!tryAttach()) {
      pollId = setInterval(() => {
        attempts += 1;
        if (tryAttach() || attempts >= 30) {
          clearInterval(pollId);
          pollId = null;
        }
      }, 100);
    }

    return () => {
      if (pollId) clearInterval(pollId);
      scrollContainer?.removeEventListener(
        'scroll',
        updateProgress
      );
      resizeObserver?.disconnect();
      setProgress(0);
    };
  }, [isLessonPage, pathname]);

  return { progress };
};

/**
 * Hook to handle keyboard shortcuts
 */
const useKeyboardShortcut = (key, modifier, callback) => {
  useEffect(() => {
    const handleKeyDown = (event) => {
      const modifierPressed =
        modifier === 'ctrl' ? event.ctrlKey : event.metaKey;

      if (
        modifierPressed &&
        event.key.toLowerCase() === key.toLowerCase()
      ) {
        event.preventDefault();
        callback();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () =>
      document.removeEventListener(
        'keydown',
        handleKeyDown
      );
  }, [key, modifier, callback]);
};

// ============================================================================
// Sub-Components
// ============================================================================

const ScrollIndicator = ({ progress, isVisible }) => {
  if (!isVisible) return null;

  return (
    <motion.div
      id='scroll-indicator'
      style={{
        scaleX: progress,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: SCROLL_INDICATOR_HEIGHT,
        originX: 0,
        backgroundColor: SCROLL_INDICATOR_COLOR,
        transformOrigin: '0%',
        zIndex: 100
      }}
    />
  );
};

const UserAvatar = ({ user, onClick }) => {
  const hasProfilePicture = Boolean(user?.picture);
  const initials = getInitials(
    user?.first_name,
    user?.last_name
  );

  return (
    <button
      type='button'
      className='flex h-10 w-10 md:h-12 md:w-12 items-center justify-center overflow-hidden rounded-full border-[1.5px] md:border-2 text-xs md:text-sm font-bold transition-colors focus:outline-none bg-primary-green text-primary-green-fg border-primary-green focus:ring-2 focus:ring-primary-green/50'
      id='user-menu-button'
      aria-expanded='false'
      aria-haspopup='true'
      data-dropdown-toggle='dropdown'
      onClick={onClick}
    >
      <span className='sr-only'>Open user menu</span>
      {hasProfilePicture ? (
        <img
          src={user.picture}
          alt={`${user?.first_name || ''} ${user?.last_name || ''}`.trim()}
          className='h-full w-full rounded-full object-cover'
        />
      ) : (
        <span>{initials}</span>
      )}
    </button>
  );
};

const AdminToggleButton = ({
  isAdminView,
  onClick
}) => (
  <button
    type='button'
    onClick={onClick}
    className={clsx(
      'inline-flex h-10 w-10 sm:h-10 sm:w-auto md:h-12 items-center justify-center rounded-xl sm:rounded-full border-2 px-0 sm:px-3 md:px-5 text-[11px] md:text-sm font-semibold whitespace-nowrap transition-colors',
      {
        'bg-primary-green text-primary-green-fg border-primary-green':
          isAdminView,
        'bg-transparent hover:bg-primary-green/10 border-primary-green text-primary-green':
          !isAdminView
      }
    )}
    aria-pressed={isAdminView}
    aria-label={isAdminView ? 'Switch to learner view' : 'Switch to admin view'}
  >
    {isAdminView ? (
      <>
        <Settings className='h-4 w-4' aria-hidden='true' />
        <span className='hidden sm:inline ml-1.5'>Admin View</span>
      </>
    ) : (
      <>
        <Eye className='h-4 w-4' aria-hidden='true' />
        <span className='hidden sm:inline ml-1.5'>Learner View</span>
      </>
    )}
  </button>
);

const UserDropdownMenu = ({ user, fullName, onLogout }) => (
  <div
    className='absolute right-0 top-full mt-2 z-50 w-56 text-base list-none bg-white divide-y divide-gray-100 shadow dark:bg-gray-700 dark:divide-gray-600 rounded-xl'
    id='dropdown'
    role='menu'
    aria-labelledby='user-menu-button'
  >
    <div className='py-3 px-4'>
      <span className='block text-sm font-semibold text-gray-900 dark:text-white'>
        {fullName}
      </span>
      <span className='block text-sm text-gray-900 truncate dark:text-white'>
        {user.email}
      </span>
    </div>

    <ul
      className='py-1 text-gray-700 dark:text-gray-300'
      role='none'
    >
      <li>
        <ThemeSwitcher />
      </li>
      <li>
        <Link
          to='/settings/profile'
          className='block py-2 px-4 text-sm hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-gray-400 dark:hover:text-white'
          role='menuitem'
        >
          My Account
        </Link>
      </li>
    </ul>

    <ul
      className='py-1 text-gray-700 dark:text-gray-300'
      role='none'
    >
      <li>
        <button
          type='button'
          onClick={onLogout}
          className='block w-full text-left py-2 px-4 text-sm hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white'
          role='menuitem'
        >
          Sign out
        </button>
      </li>
    </ul>
  </div>
);

const extractDisplayName = (name) => {
  if (!name) return '';
  return name.split('|').pop()?.trim() || name;
};

const DiscussionNotificationPanel = ({
  isLoading,
  items,
  locallyReadIds,
  totalUnread,
  unreadCountInList,
  hasMore,
  isFetchingMore,
  isMarkingAllRead,
  onMarkAllRead,
  onItemClick,
  onLoadMore,
  onPanelEnter,
  onPanelLeave
}) => {
  const sentinelRef = useRef(null);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || isFetchingMore) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onLoadMore();
        }
      },
      { root: node.parentElement, rootMargin: '40px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, isFetchingMore, onLoadMore]);

  const sortedItems = useMemo(() => {
    const withReadFlag = items.map((item) => ({
      item,
      isRead: item.is_read || locallyReadIds.has(item.thread_id)
    }));
    return withReadFlag
      .sort((a, b) => {
        if (a.isRead === b.isRead) return 0;
        return a.isRead ? 1 : -1;
      })
      .map((entry) => entry.item);
  }, [items, locallyReadIds]);

  return (
    <div
      className='fixed left-1/2 top-[72px] z-50 w-[92vw] max-w-[360px] -translate-x-1/2 rounded-xl border border-gray-200 bg-white shadow-xl dark:border-[#253143] dark:bg-[#14233A] sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[340px] sm:max-w-none sm:translate-x-0'
      role='dialog'
      aria-label='Discussion notifications'
      onMouseEnter={onPanelEnter}
      onMouseLeave={onPanelLeave}
    >
      <div className='relative px-4 py-3 border-b border-gray-200 bg-white dark:border-[#253143]/70 dark:bg-[#14233A]'>
        <p className='text-sm font-semibold text-[#1C252E] dark:text-white'>
          Discussion Notifications
        </p>
        {totalUnread > 0 && (
          <span className='absolute right-4 top-1/2 -translate-y-1/2 inline-flex h-8 min-w-8 items-center justify-center rounded-full border border-primary-green bg-primary-green px-2 text-xs font-bold leading-none text-primary-green-fg'>
            {totalUnread > 99 ? '99+' : totalUnread}
          </span>
        )}
      </div>

      <div className='max-h-[65vh] overflow-y-auto px-2 py-2 sm:max-h-[320px] notifications-scrollbar bg-white dark:bg-[#1D2838]'>
        {isLoading && items.length === 0 && (
          <p className='px-2 py-3 text-xs text-gray-500 dark:text-gray-400'>
            Loading notifications...
          </p>
        )}

        {!isLoading && items.length === 0 && (
          <p className='px-2 py-3 text-xs text-gray-500 dark:text-gray-400'>
            You are all caught up.
          </p>
        )}

        {sortedItems.map((item) => {
          const creatorName = item?.created_by
            ? [item.created_by.first_name, item.created_by.last_name]
                .filter(Boolean)
                .join(' ')
            : 'Anonymous';

          const courseName = extractDisplayName(item?.course_name);
          const moduleName = extractDisplayName(item?.module_name);
          const lessonName = extractDisplayName(item?.lesson_name);

          let ContextIcon = BookOpen;
          if (item?.board_type === 'lesson') {
            ContextIcon = FileText;
          } else if (item?.board_type === 'module') {
            ContextIcon = Layers;
          }

          const isRead =
            item.is_read || locallyReadIds.has(item.thread_id);

          return (
            <button
              type='button'
              key={item.thread_id}
              onClick={() => onItemClick(item)}
              className={clsx(
                'relative block w-full rounded-lg px-2 py-2 text-left transition hover:bg-gray-100 dark:hover:bg-[#253143]',
                !isRead && 'bg-[#1F7A5C]/10 dark:bg-[#1F7A5C]/15'
              )}
            >
              {!isRead && (
                <span
                  className='absolute left-0 top-3 bottom-3 w-1.5 rounded-r-full bg-primary-green'
                  aria-label='Unread'
                />
              )}
              <div className='flex items-start gap-3 pl-2'>
                <div
                  className={clsx(
                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                    isRead
                      ? 'bg-gray-200 text-gray-500 dark:bg-[#253143] dark:text-gray-400'
                      : 'bg-[#1F7A5C]/25 text-primary-green'
                  )}
                >
                  <MessageSquare className='h-5 w-5' />
                </div>
                <div className='min-w-0'>
                  <p
                    className={clsx(
                      'text-sm text-[#1C252E] dark:text-white',
                      isRead ? 'font-medium opacity-70' : 'font-bold'
                    )}
                  >
                    {creatorName}
                  </p>
                  <p
                    className={clsx(
                      'mt-0.5 line-clamp-2 text-sm text-[#1C252E] dark:text-white/90',
                      isRead ? 'font-normal opacity-70' : 'font-semibold'
                    )}
                  >
                    {item.topic || 'Untitled discussion'}
                  </p>
                  <div
                    className={clsx(
                      'mt-1 flex items-start gap-1.5 text-xs text-gray-500 dark:text-gray-400',
                      isRead && 'opacity-70'
                    )}
                  >
                    <ContextIcon className='mt-0.5 h-3.5 w-3.5 shrink-0' />
                    <div className='flex min-w-0 flex-col gap-0.5'>
                      <span className='truncate'>
                        {courseName || 'Course discussion'}
                      </span>
                      {(item?.board_type === 'module' ||
                        item?.board_type === 'lesson') &&
                        moduleName && (
                          <span className='truncate pl-2'>
                            &gt; {moduleName}
                          </span>
                        )}
                      {item?.board_type === 'lesson' && lessonName && (
                        <span className='truncate pl-4'>
                          &gt; {lessonName}
                        </span>
                      )}
                    </div>
                  </div>
                  <p
                    className={clsx(
                      'mt-0.5 text-xs text-gray-500 dark:text-gray-400',
                      isRead && 'opacity-70'
                    )}
                  >
                    {item.created_at
                      ? getTimeDifference(item.created_at)
                      : 'Just now'}
                  </p>
                </div>
              </div>
            </button>
          );
        })}

        {hasMore && (
          <div
            ref={sentinelRef}
            className='py-2 text-center text-xs text-gray-500 dark:text-gray-400'
          >
            {isFetchingMore ? 'Loading more...' : ''}
          </div>
        )}
      </div>

      {unreadCountInList > 0 && (
        <div className='border-t border-gray-200 bg-white p-0 dark:border-[#253143]/70 dark:bg-[#14233A]'>
          <button
            type='button'
            onClick={onMarkAllRead}
            disabled={isMarkingAllRead}
            className='m-3 w-[calc(100%-24px)] rounded-lg border border-[#C5CEDA]/70 bg-[#EEF2F7] px-3 py-3 text-sm font-semibold text-[#1C252E] transition hover:bg-[#E3E9F1] disabled:cursor-not-allowed disabled:opacity-60 dark:border-[#3A4A62] dark:bg-[#1D2838] dark:text-white dark:hover:bg-[#253143]'
          >
            {isMarkingAllRead ? 'Marking as read...' : 'Mark All as Read'}
          </button>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

const Navbar = () => {
  // Redux state
  const user = useSelector((state) => state.auth.user);

  // Router hooks
  const navigate = useNavigate();
  const location = useLocation();

  // Module/course context from persistent store
  const { courseUuid } = useModuleStore();

  // Context hooks
  const { signOut } = useAuth();
  const { isDarkMode } = useTheme();
  const { isAdmin, isAdminView, toggleAdminView, isSuperAdmin } =
    useAdmin();
  const { data: unreadData } = useFetchTotalUnreadQuery(undefined, {
    refetchOnMountOrArgChange: true,
    pollingInterval: 30000
  });
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] =
    useState(false);
  const [markAllThreadsRead, { isLoading: isMarkingAllRead }] =
    useMarkAllThreadsReadMutation();
  const [markThreadAsRead] = useMarkThreadAsReadMutation();
  const [unreadLimit, setUnreadLimit] = useState(UNREAD_DETAILS_LIMIT);
  const [locallyReadIds, setLocallyReadIds] = useState(
    () => new Set()
  );
  const [hasMoreUnread, setHasMoreUnread] = useState(true);
  const { data: unreadDetailsData, isFetching: isUnreadDetailsFetching } =
    useFetchUnreadDetailsQuery(
      { limit: unreadLimit, includeRead: true },
      {
        skip: !isNotificationPanelOpen,
        refetchOnMountOrArgChange: true
      }
    );
  const notificationItems = useMemo(
    () => unreadDetailsData?.unread || [],
    [unreadDetailsData]
  );

  // Local state
  const [isUserDropdownOpen, setIsUserDropdownOpen] =
    useState(false);
  // Bumped by Ctrl+K to open the search panel (CON-306).
  const [searchOpenSignal, setSearchOpenSignal] = useState(0);
  const notificationCloseTimerRef = useRef(null);

  // Derived values (memoized)
  const fullName = useMemo(
    () =>
      `${user?.first_name ?? ''} ${user?.last_name ?? ''
        }`.trim(),
    [user?.first_name, user?.last_name]
  );

  const university = useMemo(
    () => findUniversityWithLogo(user),
    [user]
  );

  const logoSrc = useMemo(
    () => getLogo(university, isDarkMode),
    [university, isDarkMode]
  );

  const isModulesDetailPage = useMemo(
    () =>
      location.pathname.includes('/modules/') &&
      location.pathname.includes('/lessons/'),
    [location.pathname]
  );

  const { progress: scrollProgress } = useLessonScrollProgress(
    isModulesDetailPage,
    location.pathname
  );
  const totalUnread = Math.max(
    0,
    Number(unreadData?.total_unread || 0)
  );
  const unreadCountInList = useMemo(
    () =>
      notificationItems.reduce((n, item) => {
        const isRead =
          item.is_read || locallyReadIds.has(item.thread_id);
        return isRead ? n : n + 1;
      }, 0),
    [notificationItems, locallyReadIds]
  );

  // Media query
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Callbacks (memoized)
  const toggleUserDropdown = useCallback(() => {
    setIsUserDropdownOpen((prev) => !prev);
  }, []);

  const handleLogout = useCallback(() => {
    signOut();
    navigate('/login');
  }, [signOut, navigate]);

  const handleDiscussionIconClick = useCallback(() => {
    const { pathname } = location;

    const lessonMatch = pathname.match(
      /^\/modules\/([^/]+)\/lessons\/([^/]+)/
    );
    if (lessonMatch) {
      const moduleId = lessonMatch[1];
      const lessonId = lessonMatch[2];
      navigate('/discussion', {
        state: {
          from: 'lessons',
          returnPath: pathname,
          courseUuid,
          moduleId,
          lessonId
        }
      });
      return;
    }

    const moduleMatch = pathname.match(/^\/modules\/([^/]+)/);
    if (moduleMatch) {
      const moduleId = moduleMatch[1];
      navigate('/discussion', {
        state: {
          from: 'modules',
          returnPath: pathname,
          courseUuid,
          moduleId
        }
      });
      return;
    }

    const courseMatch = pathname.match(/^\/courses\/([^/]+)/);
    if (courseMatch) {
      navigate('/discussion', {
        state: {
          from: 'modules',
          returnPath: pathname,
          courseUuid
        }
      });
      return;
    }

    navigate('/discussion', {
      state: {
        from: 'courses',
        returnPath: '/dashboard'
      }
    });
  }, [location, navigate, courseUuid]);

  const handleMarkAllNotificationsRead = useCallback(async () => {
    setLocallyReadIds((prev) => {
      const next = new Set(prev);
      notificationItems.forEach((item) => next.add(item.thread_id));
      return next;
    });
    try {
      await markAllThreadsRead().unwrap();
    } catch (error) {
      // no-op; avoid interruptive toast from navbar controls
    }
  }, [markAllThreadsRead, notificationItems]);

  const handleNotificationItemClick = useCallback(
    (item) => {
      if (!item?.thread_id) return;
      setLocallyReadIds((prev) => {
        if (prev.has(item.thread_id)) return prev;
        const next = new Set(prev);
        next.add(item.thread_id);
        return next;
      });
      if (item.board_slug && item.thread_slug) {
        markThreadAsRead({
          boardSlug: item.board_slug,
          threadSlug: item.thread_slug
        }).catch(() => {});
        setIsNotificationPanelOpen(false);
        let from = 'courses';
        if (item.board_type === 'lesson') from = 'lessons';
        else if (item.board_type === 'module') from = 'modules';
        navigate('/discussion', {
          state: {
            from,
            returnPath: location.pathname,
            courseUuid: item.course_id || null,
            moduleId: item.module_id || null,
            lessonId: item.lesson_id || null,
            targetBoardSlug: item.board_slug,
            targetThreadSlug: item.thread_slug,
            targetCommentId: item.last_unread_comment_id || null
          }
        });
      }
    },
    [markThreadAsRead, navigate, location.pathname]
  );

  const handleLoadMoreNotifications = useCallback(() => {
    if (isUnreadDetailsFetching || !hasMoreUnread) return;
    setUnreadLimit((prev) => prev + UNREAD_DETAILS_LIMIT);
  }, [isUnreadDetailsFetching, hasMoreUnread]);

  const openNotificationPanel = useCallback(() => {
    if (notificationCloseTimerRef.current) {
      clearTimeout(notificationCloseTimerRef.current);
      notificationCloseTimerRef.current = null;
    }
    setIsNotificationPanelOpen(true);
  }, []);

  const closeNotificationPanelWithDelay = useCallback(() => {
    if (notificationCloseTimerRef.current) {
      clearTimeout(notificationCloseTimerRef.current);
    }
    notificationCloseTimerRef.current = setTimeout(() => {
      setIsNotificationPanelOpen(false);
      notificationCloseTimerRef.current = null;
    }, 180);
  }, []);

  const focusSearchInput = useCallback(() => {
    setSearchOpenSignal((n) => n + 1);
  }, []);

  // Keyboard shortcut for search
  useKeyboardShortcut('k', 'ctrl', focusSearchInput);

  // Close dropdown when clicking outside
  useClickAnyWhere((event) => {
    if (
      isUserDropdownOpen &&
      !event.target.closest('[data-dropdown-toggle], [data-keep-menu-open]')
    ) {
      setIsUserDropdownOpen(false);
    }
  });

  // Mobile handling effect (placeholder for future functionality)
  useEffect(() => {
    if (isMobile) {
      // Handle mobile-specific behavior if needed
    }
  }, [isMobile]);

  useEffect(
    () => () => {
      if (notificationCloseTimerRef.current) {
        clearTimeout(notificationCloseTimerRef.current);
        notificationCloseTimerRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    if (!isNotificationPanelOpen) {
      setLocallyReadIds(new Set());
      setUnreadLimit(UNREAD_DETAILS_LIMIT);
      setHasMoreUnread(true);
    }
  }, [isNotificationPanelOpen]);

  useEffect(() => {
    if (!isNotificationPanelOpen) return;
    const fresh = unreadDetailsData?.unread;
    if (!Array.isArray(fresh)) return;
    setHasMoreUnread(fresh.length >= unreadLimit);
  }, [unreadDetailsData, unreadLimit, isNotificationPanelOpen]);

  return (
    <>
      <ScrollIndicator
        progress={scrollProgress}
        isVisible={isModulesDetailPage}
      />

      <nav
        className='bg-white dark:bg-[#1D2838] fixed left-0 right-0 top-0 z-50'
        role='navigation'
        aria-label='Main navigation'
      >
        <PageContent className='flex justify-between items-center'>
          {/* Logo Section */}
          <div className='flex justify-start items-center shrink-0'>
            <Link
              to='/'
              className={clsx(
                'flex items-center justify-between mr-2 md:mr-4 h-16 md:h-[5.16rem] md:-ml-2'
              )}
              aria-label='Go to homepage'
            >
              <EditableLogo university={university}>
                <NavbarLogo
                  src={logoSrc}
                  fallbackSrc={isDarkMode ? LOGO_WHITE : LOGO_BLACK}
                  alt='Company logo'
                />
              </EditableLogo>
            </Link>
          </div>

          {/* Actions Section */}
          <div className='flex items-center gap-1.5 sm:gap-2 min-w-0 py-2.5'>
            <div className='hidden sm:flex items-center justify-center'>
              <span className='font-semibold text-sm truncate max-w-[220px] text-primary-green'>
                {fullName}
              </span>
            </div>

            <div className='relative'>
              <UserAvatar
                user={user}
                onClick={toggleUserDropdown}
              />
              {isUserDropdownOpen && (
                <UserDropdownMenu
                  user={user}
                  fullName={fullName}
                  onLogout={handleLogout}
                />
              )}
            </div>

            {/* Global discussion board shortcut */}
            <div
              className='relative'
              onMouseEnter={openNotificationPanel}
              onMouseLeave={closeNotificationPanelWithDelay}
            >
              <button
                type='button'
                onClick={handleDiscussionIconClick}
                onFocus={openNotificationPanel}
                className='flex h-10 w-10 md:h-12 md:w-12 items-center justify-center rounded-full transition-colors bg-primary-green hover:bg-primary-green/90 text-primary-green-fg'
                aria-label='Open discussion board'
              >
                <MessagesSquare
                  className='h-4 w-4 md:h-5 md:w-5'
                  aria-hidden='true'
                />
              </button>
              {totalUnread > 0 && (
                <span className='absolute -right-1 -top-1 inline-flex min-h-5 min-w-5 items-center justify-center rounded-full border border-primary-green bg-[#0F172A] px-1 text-[10px] font-bold leading-none text-primary-green'>
                  {totalUnread > 99 ? '99+' : totalUnread}
                </span>
              )}
              {isNotificationPanelOpen && (
                <DiscussionNotificationPanel
                  isLoading={isUnreadDetailsFetching}
                  items={notificationItems}
                  locallyReadIds={locallyReadIds}
                  totalUnread={totalUnread}
                  unreadCountInList={unreadCountInList}
                  hasMore={hasMoreUnread}
                  isFetchingMore={isUnreadDetailsFetching}
                  isMarkingAllRead={isMarkingAllRead}
                  onMarkAllRead={handleMarkAllNotificationsRead}
                  onItemClick={handleNotificationItemClick}
                  onLoadMore={handleLoadMoreNotifications}
                  onPanelEnter={openNotificationPanel}
                  onPanelLeave={closeNotificationPanelWithDelay}
                />
              )}
            </div>

            {/* Search sits right of the discussion board, left of Super Admin (Dane, CON-306) */}
            <LearnerSearch openSignal={searchOpenSignal} />

            {isSuperAdmin && (
              <button
                type='button'
                onClick={() => navigate('/super-admin')}
                className='flex h-10 w-10 md:h-12 md:w-12 items-center justify-center rounded-full transition-colors bg-primary-green hover:bg-primary-green/90 text-primary-green-fg'
                aria-label='Open Super Admin dashboard'
                title='Super Admin'
              >
                <Shield className='h-4 w-4 md:h-5 md:w-5' aria-hidden='true' />
              </button>
            )}

            {isAdmin && (
              <AdminToggleButton
                isAdminView={isAdminView}
                onClick={toggleAdminView}
              />
            )}

          </div>
        </PageContent>
      </nav>
    </>
  );
};

export default Navbar;
